import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "jianpian_favorites";
const CHANGE_EVENT = "jianpian-favorites-change";

export function readFavorites(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as string[]) : [];
  } catch {
    return [];
  }
}

function writeFavorites(list: string[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    window.dispatchEvent(new Event(CHANGE_EVENT));
  } catch {
    /* storage unavailable */
  }
}

export function toggleFavorite(id: string): string[] {
  const current = readFavorites();
  const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
  writeFavorites(next);
  return next;
}

export function useFavorites() {
  const [favorites, setFavorites] = useState<string[]>(() => readFavorites());

  useEffect(() => {
    const sync = () => setFavorites(readFavorites());
    window.addEventListener(CHANGE_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(CHANGE_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const toggle = useCallback((id: string) => {
    toggleFavorite(id);
  }, []);

  const clear = useCallback(() => {
    writeFavorites([]);
  }, []);

  const isFavorite = useCallback((id: string) => favorites.includes(id), [favorites]);

  return { favorites, toggle, clear, isFavorite };
}